import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { cleanState, EditorState, isUpdating } from '@ublend-npm/aula-editor';
import {
  Box,
  Button,
  Divider,
  IconButton,
  Icons,
  Modal,
  Typography,
  useMediaQuery,
  useTheme,
} from '@ublend-npm/aulaui-core';
import feedPostTemplates from '../../../Feed/CreatePost/feedPostTemplates';
import isEmptyContent from '../../../Feed/isEmptyContent';
import { createStyles } from './PostModal.styles';
import {
  CANCEL,
  DEFAULT_ITEM_CLASS,
  getModalTitle,
  ITEM_CLASS_MAP,
  MESSAGES,
  POST,
} from './text';
import type { PostModalProps } from './types';
import NavigationBackHeader from '@app/components/presentational/common/NavigationBackHeader/NavigationBackHeader';
import CloseButton from '@app/components/presentational/common/CloseButton/CloseButton';

const CLOSE_DELAY_MS = 1000;

// This function is used to get the initial editor state from the content.
const getInitialEditorState = (content?: string): EditorState => {
  if (!content) {
    return cleanState as EditorState;
  }

  try {
    return JSON.parse(content);
  } catch {
    return cleanState as EditorState;
  }
};

const PostModal = ({
  open,
  spaceId,
  onClose = () => {},
  onSubmit,
  isSavingPost = false,
  editorConfig,
  EditorComponent,
  content,
  itemClass = DEFAULT_ITEM_CLASS,
  mode = 'edit',
  title,
  placeholder,
  footerExtra,
  isSinglePageMode = false,
  handleBack = () => {},
  itemId,
}: PostModalProps) => {
  const theme = useTheme() as any;
  const styles = createStyles(theme);
  const isCreate = mode === 'create';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const initialEditorState = useMemo<EditorState>(
    () => getInitialEditorState(content),
    [content]
  );

  const [editorState, setEditorState] =
    useState<EditorState>(initialEditorState);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [enablePostButtons, setEnablePostButtons] = useState<boolean>(
    !isCreate
  );
  const [saving, setSaving] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');
  const closeTimeout = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => clearTimeout(closeTimeout.current), []);

  useEffect(() => {
    setIsSubmitting(isUpdating(editorState, editorConfig));
  }, [editorState, editorConfig]);

  const handleRequestClose = () => {
    if (saving) return;
    setEditorState(initialEditorState);
    onClose();
  };

  const handleEditorChange = useCallback((state: EditorState) => {
    setEditorState(state);
  }, []);

  const statusMessages = MESSAGES[mode];

  const handleSubmit = async () => {
    setSaving(true);
    try {
      await onSubmit(editorState);
      setMessage(statusMessages.success);
      closeTimeout.current = setTimeout(() => {
        setSaving(false);
        setMessage('');
        onClose();
      }, CLOSE_DELAY_MS);
    } catch (error) {
      setSaving(false);
      setMessage(statusMessages.error);
      console.log(error);
    } finally {
      if (isMobile && isSinglePageMode) {
        handleBack();
      }
    }
  };

  // Show loading state when the post is being saved, the editor is submitting, or the saving state is true
  const loading = isSavingPost || isSubmitting || saving;
  const isPostItem = itemClass === DEFAULT_ITEM_CLASS;
  const itemLabel =
    ITEM_CLASS_MAP[itemClass] || ITEM_CLASS_MAP[DEFAULT_ITEM_CLASS];
  const analyticsType = isCreate
    ? 'createpost'
    : itemLabel === 'message'
    ? 'inbox'
    : itemLabel;
  const heading = title ?? getModalTitle(mode, itemLabel);

  // Disable the submit button when the post is being saved, the editor is submitting, or the saving state is true
  const disableButton =
    saving || !enablePostButtons || (isCreate && isEmptyContent(editorState));

  // Editor component
  const editor = (
    <>
      <Box
        sx={{
          ...styles.editorWrapper,
          p: loading ? '0 1.5rem 1rem 2.5rem' : '0',
        }}
        data-testid={isCreate ? 'create-post-editor' : 'edit-post-editor'}
      >
        <EditorComponent
          type={analyticsType}
          config={editorConfig}
          editorState={editorState}
          placeholder={placeholder}
          onChange={handleEditorChange}
          isSubmitting={loading}
          setSubmittingState={setIsSubmitting}
          isAssignmentsEnabled
          templates={isPostItem ? feedPostTemplates : []}
          contentType={isPostItem ? 'post' : null}
          onFilesStatusChange={({
            isUpdating: filesUpdating,
            isPersistable: filesPersistable,
            hasError,
          }) => {
            setEnablePostButtons(
              !filesUpdating && !hasError && filesPersistable
            );
          }}
        />
      </Box>
    </>
  );

  if (isMobile && isSinglePageMode) {
    return (
      <PostMobileWrapper
        styles={styles}
        loading={loading}
        disableButton={disableButton}
        handleSubmit={handleSubmit}
        handleBack={handleBack}
        isEditMode={true}
      >
        {editor}
      </PostMobileWrapper>
    );
  }

  return (
    <Modal open={open} onClose={handleRequestClose}>
      <Box sx={styles.modalWrapper}>
        <Typography component="span" variant="h6">
          {heading}
        </Typography>
        {editor}
        <Box sx={styles.footer}>
          {footerExtra ? (
            <Box sx={styles.footerExtra}>{footerExtra}</Box>
          ) : null}
          <Button
            sx={{ ...styles.buttonSx, width: { xs: 'auto', sm: '90px' } }}
            kind="secondary"
            type="button"
            onClick={handleRequestClose}
            disabled={saving}
          >
            {CANCEL}
          </Button>
          <Button
            sx={styles.buttonSx}
            kind="primary"
            type="button"
            loading={saving}
            onClick={handleSubmit}
            disabled={disableButton}
          >
            {POST}
          </Button>
          <Box sx={styles.visuallyHidden} aria-live="polite" role="status">
            {message}
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default PostModal;

const PostMobileWrapper = ({
  styles,
  handleBack,
  children,
  loading,
  disableButton,
  handleSubmit,
  isEditMode,
}: {
  styles: any;
  handleBack: () => void;
  children: React.ReactNode;
  loading: boolean;
  disableButton: boolean;
  handleSubmit: () => void;
  isEditMode: boolean;
}) => {
  return (
    <Box sx={styles.mobileContentWrapper}>
      <Box sx={styles.mobileHeader}>
        {isEditMode ? (
          <CloseButton onClick={handleBack} label="Close" />
        ) : (
          <NavigationBackHeader onClick={handleBack} />
        )}
        <Box sx={styles.mobileHeaderRightEnd}>
          {/* TODO: Schedule post */}
          <IconButton onClick={() => {}} aria-label="Schedule post">
            <Icons.Schedule />
          </IconButton>
          <Button
            sx={styles.mobileButtonSx}
            kind="primary"
            type="button"
            loading={loading}
            onClick={handleSubmit}
            disabled={disableButton}
          >
            {POST}
          </Button>
        </Box>
      </Box>

      <Divider sx={{ my: '10px' }} />
      <Box sx={styles.mobileEditor}>{children}</Box>
    </Box>
  );
};
